import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, Eraser, PenLine } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "./button";

// Captura de firma. value: data URL capturada; firmaRegistrada: firma del perfil, opcional.
const FirmaCanvas = ({
  label = "Firma",
  value = "",
  onChange,
  firmaRegistrada = null,
  disabled = false,
}) => {
  const sigCanvas = useRef(null);
  const contenedor = useRef(null);
  const [usaRegistrada, setUsaRegistrada] = useState(false);

  useEffect(() => {
    const ajustar = () => {
      const caja = contenedor.current;
      const sig = sigCanvas.current;
      if (!caja || !sig) return;
      const canvas = sig.getCanvas();
      const ancho = caja.clientWidth || 600;
      const alto = caja.clientHeight || 200;
      const ratio = window.devicePixelRatio || 1;
      // Cambiar las dimensiones borra el canvas: solo cuando cambia el tamaño real
      if (canvas.width === Math.round(ancho * ratio)) return;
      canvas.width = Math.round(ancho * ratio);
      canvas.height = Math.round(alto * ratio);
      canvas.style.width = `${ancho}px`;
      canvas.style.height = `${alto}px`;
      canvas.getContext("2d").setTransform(ratio, 0, 0, ratio, 0, 0);
      sig.clear();
    };
    ajustar();
    const observador = new ResizeObserver(ajustar);
    if (contenedor.current) observador.observe(contenedor.current);
    return () => observador.disconnect();
  }, [usaRegistrada]);

  const limpiar = () => {
    sigCanvas.current?.clear();
    onChange("");
  };

  const capturar = () => {
    const sig = sigCanvas.current;
    if (!sig || sig.isEmpty()) {
      toast.error("La firma está vacía");
      return;
    }
    onChange(sig.getCanvas().toDataURL("image/png"));
  };

  const alternarRegistrada = () => {
    const siguiente = !usaRegistrada;
    setUsaRegistrada(siguiente);
    onChange(siguiente ? firmaRegistrada : "");
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        {firmaRegistrada && (
          <button
            type="button"
            onClick={alternarRegistrada}
            disabled={disabled}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
          >
            {usaRegistrada ? "Trazar otra firma" : "Usar mi firma registrada"}
          </button>
        )}
      </div>

      {usaRegistrada ? (
        <div className="flex h-44 items-center justify-center rounded-xl border-2 border-gray-200 bg-white p-3">
          <img
            src={firmaRegistrada}
            alt="Firma registrada"
            className="max-h-full max-w-full object-contain"
          />
        </div>
      ) : (
        <div ref={contenedor} className="h-52 w-full sm:h-44">
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            onEnd={() => value && onChange("")}
            canvasProps={{
              className:
                "h-full w-full touch-none rounded-xl border-2 border-gray-300 bg-white shadow-inner",
            }}
          />
        </div>
      )}

      {!usaRegistrada && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={limpiar} disabled={disabled}>
            <Eraser size={14} className="mr-1.5" />
            Limpiar
          </Button>
          <Button type="button" variant="outline" onClick={capturar} disabled={disabled}>
            <PenLine size={14} className="mr-1.5" />
            Capturar firma
          </Button>
        </div>
      )}
      {value && (
        <p className="flex items-center gap-1.5 text-xs font-medium text-green-700">
          <CheckCircle2 size={14} />
          Firma lista
        </p>
      )}
    </div>
  );
};

export default FirmaCanvas;
